
<template>
  <div class="podcast-container" :style="backgroundStyle">
    <!-- Main stage area for active speaker - more natural design -->
    <div class="main-stage" v-if="activeSpeaker">
      <div class="active-speaker-container">
        <div class="active-speaker-wrapper" :class="{ 'is-playing': isPlaying }">
          <!-- Speaker image container with relative positioning -->
          <div class="active-speaker-image-container">
            <img
              :src="getActiveSpeakerImage()"
              :alt="`${activeSpeaker} avatar`"
              class="active-speaker-image"
            />

            <!-- Add the wave animation indicator (only visible when playing) -->
            <div
              v-if="isPlaying"
              class="active-image-wave-indicator"
              :style="{ backgroundColor: `rgba(0, 0, 0, 0.6)`, borderColor: getTextColor(getActiveSpeakerIndex()) }"
            >
              <div
                v-for="n in 5"
                :key="`main-wave-${n}`"
                class="main-wave-bar"
                :style="{ backgroundColor: getTextColor(getActiveSpeakerIndex()) }"
              ></div>
            </div>
          </div>

          <div class="active-speaker-info">
            <span class="active-speaker-name">{{ getActiveSpeakerObj().name }}</span>
            <span class="active-speaker-role">{{ getActiveSpeakerObj().role }}</span>
          </div>
          <!-- Enhanced speaking animation -->
          <div v-if="isPlaying" class="audio-visualizer">
            <div v-for="n in 5" :key="`vis-${n}`" class="visualizer-bar"
                :style="{ animationDelay: `${n * 0.1}s`, backgroundColor: getTextColor(getActiveSpeakerIndex()) }"></div>
          </div>
        </div>
      </div>
    </div>

    <!-- Minimalist speaker strip at bottom, always shown -->
    <div class="speaker-strip" :class="{ 'standalone': alwaysShowSpeakers }">
      <div
        v-for="(speaker, index) in internalSpeakers"
        :key="`speaker-${index}`"
        class="speaker-thumbnail"
        :class="{
          'active': activeSpeaker === speaker.name,
          'is-speaking': activeSpeaker === speaker.name && isPlaying
        }"
        @click="$emit('speaker-selected', speaker.name)"
      >
        <div class="thumbnail-wrapper">
          <img
            :src="getSpeakerImage(index)"
            :alt="`${speaker.name} avatar`"
            class="thumbnail-image"
          />

          <!-- Mini wave animation when speaking -->
          <div
            v-if="activeSpeaker === speaker.name && isPlaying"
            class="thumbnail-wave-indicator"
          >
            <div
              v-for="n in 3"
              :key="`thumb-wave-${n}`"
              class="thumbnail-wave-bar"
              :style="{ backgroundColor: getTextColor(index) }"
            ></div>
          </div>
        </div>

        <span
          class="thumbnail-name"
          :style="{ color: activeSpeaker === speaker.name ? getTextColor(index) : '' }"
        >
          {{ speaker.name }}
        </span>
      </div>
    </div>

    <!-- Settings panel (keeping this part mostly the same) -->
    <div v-if="!isPlaying && !isMobile" class="settings-panel" :class="{ 'settings-open': showSettings }">
      <button class="settings-toggle" @click="toggleSettings" title="Customize your experience by uploading photos, changing names and roles, or selecting different AI voices for speakers.">
        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <path d="M12.22 2h-.44a2 2 0 0 0-2 2v.18a2 2 0 0 1-1 1.73l-.43.25a2 2 0 0 1-2 0l-.15-.08a2 2 0 0 0-2.73.73l-.22.38a2 2 0 0 0 .73 2.73l.15.1a2 2 0 0 1 1 1.72v.51a2 2 0 0 1-1 1.74l-.15.09a2 2 0 0 0-.73 2.73l.22.38a2 2 0 0 0 2.73.73l.15-.08a2 2 0 0 1 2 0l.43.25a2 2 0 0 1 1 1.73V20a2 2 0 0 0 2 2h.44a2 2 0 0 0 2-2v-.18a2 2 0 0 1 1-1.73l.43-.25a2 2 0 0 1 2 0l.15.08a2 2 0 0 0 2.73-.73l.22-.39a2 2 0 0 0-.73-2.73l-.15-.08a2 2 0 0 1-1-1.74v-.5a2 2 0 0 1 1-1.74l.15-.09a2 2 0 0 0 .73-2.73l-.22-.38a2 2 0 0 0-2.73-.73l-.15.08a2 2 0 0 1-2 0l-.43-.25a2 2 0 0 1-1-1.73V4a2 2 0 0 0-2-2z"></path>
          <circle cx="12" cy="12" r="3"></circle>
        </svg>
        <span>{{ showSettings ? 'Close' : 'Customize Speakers' }}</span>
      </button>

      <!-- Settings content (unchanged) -->
      <div v-if="showSettings" class="settings-content">
        <!-- Background image upload -->
        <div class="settings-section">
          <h3 class="settings-title">Podcast Background</h3>
          <div class="background-upload">
            <label for="background-upload" class="upload-bg-label">
              <div class="bg-preview-container">
                <img
                  v-if="backgroundImage"
                  :src="backgroundImage"
                  class="bg-preview"
                  alt="Background Preview"
                />
                <div v-else class="bg-placeholder">
                  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                    <rect width="18" height="18" x="3" y="3" rx="2" ry="2"></rect>
                    <circle cx="9" cy="9" r="2"></circle>
                    <path d="m21 15-3.086-3.086a2 2 0 0 0-2.828 0L6 21"></path>
                  </svg>
                  <span>Upload Background</span>
                </div>
              </div>
            </label>
            <input
              id="background-upload"
              type="file"
              accept="image/*"
              class="file-input"
              @change="handleBackgroundUpload"
            />
          </div>
        </div>

        <!-- Speaker customization -->
        <div class="settings-section">
          <h3 class="settings-title">Customize Speakers</h3>
          <div class="speakers-grid">
            <div
              v-for="(speaker, index) in internalSpeakers"
              :key="`customize-${index}`"
              class="speaker-customize-item"
            >
              <div class="speaker-image-upload">
                <label :for="`image-upload-${messageId}_${index}`" class="upload-label">
                  <div class="preview-container">
                    <img
                      v-if="uploadedImages[`${messageId}_${index}`]"
                      :src="uploadedImages[`${messageId}_${index}`]"
                      class="image-preview"
                      alt="Preview"
                    />
                    <div v-else class="upload-placeholder">
                      <span>{{ speaker.name }}</span>
                      <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                        <path d="M12 5v14"></path>
                        <path d="M5 12h14"></path>
                      </svg>
                    </div>
                  </div>
                </label>
                <input
                  :id="`image-upload-${messageId}_${index}`"
                  type="file"
                  accept="image/*"
                  class="file-input"
                  @change="(e) => handleImageUpload(e, index)"
                />
              </div>

              <div class="speaker-fields">
                <input
                  type="text"
                  v-model="internalSpeakers[index].name"
                  class="name-input"
                  placeholder="Name"
                  @change="handleNameChange(index)"
                />

                <input
                  type="text"
                  v-model="internalSpeakers[index].role"
                  class="role-input"
                  placeholder="Role"
                  @change="handleRoleChange(index)"
                />

                <select
                  v-model="selectedVoices[index]"
                  class="voice-select"
                  @change="handleVoiceChange(index)"
                >
                  <option value="">Default Voice</option>
                  <optgroup label="Female">
                    <option v-for="voice in femaleVoices" :key="`female-${voice}`" :value="voice">
                      {{ getReadableVoiceName(voice) }}
                    </option>
                  </optgroup>
                  <optgroup label="Male">
                    <option v-for="voice in maleVoices" :key="`male-${voice}`" :value="voice">
                      {{ getReadableVoiceName(voice) }}
                    </option>
                  </optgroup>
                </select>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>


<script>
import API from '@/api';
import { mapActions } from 'vuex';
export default {
  name: 'DynamicPodcastUI',
  props: {
    isPro: {
      type: Boolean,
      default: false
    },
    isMobile: {
      type: Boolean,
      default: false
    },
    messageId: {
      required: true
    },
    alwaysShowSpeakers: {
      type: Boolean,
      default: false
    },
    minimizeWhenInactive: {
      type: Boolean,
      default: false
    },
    speakers: {
      type: Array,
      required: true,
      default: () => []
    },
    activeSpeaker: {
      type: String,
      default: null
    },
    isPlaying: {
      type: Boolean,
      default: false
    },
    isExpandedView: {
      type: Boolean,
      default: true
    },
    enableImageUpload: {
      type: Boolean,
      default: true
    },
    mappedDocuments: {
      type: Array,
      default: () => []
    }
  },
  data() {
    return {
      // State for settings panel
      showSettings: false,
      // Create a deep copy of speakers to safely modify
      internalSpeakers: JSON.parse(JSON.stringify(this.speakers)),
      // Array of fallback images that will be used for speakers
      allImages: [
        '/static/Gemini_Generated_Image_yznx90yznx90yznx.jpeg',
        '/static/Gemini_Generated_Image_3uyccg3uyccg3uyc.jpeg',
        '/static/Gemini_Generated_Image_r53bp9r53bp9r53b.jpeg',
        '/static/Gemini_Generated_Image_ns59fwns59fwns59.jpeg',
        '/static/Gemini_Generated_Image_yznx90yznx90yznx.jpeg',
        '/static/Gemini_Generated_Image_m68ngom68ngom68n.jpeg',
        '/static/Gemini_Generated_Image_qc0oqtqc0oqtqc0o.jpeg',
      ],
      // Store uploaded images
      uploadedImages: {},
      // Background image
      backgroundImage: null,
      // Store selected voices
      selectedVoices: {},
      // Available voices by gender
      femaleVoices: [
        'af_alloy', 'af_aoede', 'af_bella', 'af_heart', 'af_jadzia', 'af_jessica',
        'af_kore', 'af_nicole', 'af_nova', 'af_river', 'af_sarah', 'af_sky',
        'af_v0', 'af_v0bella', 'af_v0irulan', 'af_v0nicole', 'af_v0sarah', 'af_v0sky',
        'bf_alice', 'bf_emma', 'bf_lily', 'bf_v0emma', 'bf_v0isabella',
        'ef_dora', 'ff_siwis', 'hf_alpha', 'hf_beta', 'if_sara', 'jf_alpha',
        'jf_gongitsune', 'jf_nezumi', 'jf_tebukuro', 'pf_dora', 'zf_xiaobei',
        'zf_xiaoni', 'zf_xiaoxiao', 'zf_xiaoyi'
      ],
      maleVoices: [
        'am_adam', 'am_echo', 'am_eric', 'am_fenrir', 'am_liam', 'am_michael',
        'am_onyx', 'am_puck', 'am_santa', 'am_v0adam', 'am_v0gurney', 'am_v0michael',
        'bm_daniel', 'bm_fable', 'bm_george', 'bm_lewis', 'bm_v0george', 'bm_v0lewis',
        'em_alex', 'em_santa', 'hm_omega', 'hm_psi', 'im_nicola', 'jm_kumo',
        'pm_alex', 'pm_santa', 'zm_yunjian', 'zm_yunxi', 'zm_yunxia', 'zm_yunyang'
      ],
      colors: [
        { bg: '#E3F2FD', text: '#1E88E5' }, // Blue
        { bg: '#F3E5F5', text: '#8E24AA' }, // Purple
        { bg: '#E8F5E9', text: '#43A047' }, // Green
        { bg: '#FFF3E0', text: '#FB8C00' }, // Orange
        { bg: '#F3E5F5', text: '#5E35B1' }, // Deep Purple
        { bg: '#E0F2F1', text: '#00897B' }, // Teal
        { bg: '#FBE9E7', text: '#F4511E' }, // Deep Orange
        { bg: '#F1F8E9', text: '#7CB342' }  // Light Green
      ],
    };
  },
  computed: {
    backgroundStyle() {
      return this.backgroundImage ? {
        backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.7), rgba(0, 0, 0, 0.7)), url(${this.backgroundImage})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center'
      } : {};
    }
  },

  watch: {
    // Watch for changes to props speakers array
    speakers: {
      handler(newSpeakers) {
        // Deep copy to avoid reference issues
        this.internalSpeakers = JSON.parse(JSON.stringify(newSpeakers));
      },
      deep: true,
      immediate: true
    },

    // Watch for changes to activeSpeaker
    activeSpeaker: {
      handler(newActiveSpeaker) {
        // If we have a new active speaker, make sure it's reflected in the UI
        if (newActiveSpeaker) {
          // Force UI update
          this.$forceUpdate();
        }
      },
      immediate: true
    }
  },

  // Add this to the mounted hook
  async mounted() {
    try {
      // First, apply any custom data that may have been passed via props
      // (This would be data that the parent component already loaded)
      this.applyExistingCustomizations();

      // Then load customization data from backend
      await this.loadCustomizationData();

      // Emit that customizations are loaded and ready
      this.$emit('customizations-loaded', {
        speakers: this.internalSpeakers.map((speaker, index) => ({
          original_name: this.speakers[index]?.name || speaker.name,
          custom_name: speaker.name,
          original_role: this.speakers[index]?.role || speaker.role,
          custom_role: speaker.role,
          voice_id: this.selectedVoices[index] || null
        }))
      });
    } catch (error) {
      console.error('Error loading customization data:', error);
    }
  },
  methods: {
    ...mapActions('websocket', ['showTempMessageFn']),
    async loadCustomizationData() {
      try {
        const token = localStorage.getItem('token');
        const unstruct_anonymous_key = localStorage.getItem("unstruct_anonymous_key");

        // Get background and image customizations
        const url = token ? `/user_customization/messages/${this.messageId}/customize-data` : `/user_customization/anonymous/${unstruct_anonymous_key}/messages/${this.messageId}/customize-data`;
        const response = await API.get(url);
        const data = response.data;

        // Load speaker images
        if (data.speakerImages) {
          Object.keys(data.speakerImages).forEach(index => {
            const img = data.speakerImages[index];
            const uniqueKey = `${this.messageId}_${index}`;
            this.$set(this.uploadedImages, uniqueKey, img.url);
          });
        }

        // Load background image
        if (data.background) {
          this.backgroundImage = data.background.url;
        }

        // Get speaker customizations
        const speakers_url = token ? `/user_customization/messages/${this.messageId}/speaker-customizations` : `/user_customization/anonymous/${unstruct_anonymous_key}/messages/${this.messageId}/speaker-customizations`;
        const speakerCustomResponse = await API.get(speakers_url);
        const customData = speakerCustomResponse.data;

        if (customData && customData.speakers && customData.speakers.length) {
          // Apply customizations to internal speakers using simple index-based approach
          customData.speakers.forEach(customSpeaker => {
            // If speaker_index is provided, use it directly
            if (customSpeaker.speaker_index !== undefined &&
                customSpeaker.speaker_index < this.internalSpeakers.length) {

              const index = customSpeaker.speaker_index;

              // Apply custom name
              if (customSpeaker.custom_name) {
                this.internalSpeakers[index].name = customSpeaker.custom_name;
              }

              // Apply custom role
              if (customSpeaker.custom_role) {
                this.internalSpeakers[index].role = customSpeaker.custom_role;
              }

              // Apply voice selection
              if (customSpeaker.voice_id) {
                this.$set(this.selectedVoices, index, customSpeaker.voice_id);
              }
            }
            // Fall back to matching by name if no index or index is invalid
            else {
              // Find speaker by name
              const speakerIndex = this.internalSpeakers.findIndex(s =>
                s.name === customSpeaker.original_name
              );

              if (speakerIndex >= 0) {
                // Apply custom name
                if (customSpeaker.custom_name) {
                  this.internalSpeakers[speakerIndex].name = customSpeaker.custom_name;
                }

                // Apply custom role
                if (customSpeaker.custom_role) {
                  this.internalSpeakers[speakerIndex].role = customSpeaker.custom_role;
                }

                // Apply voice selection
                if (customSpeaker.voice_id) {
                  this.$set(this.selectedVoices, speakerIndex, customSpeaker.voice_id);
                }
              }
            }
          });

          // Force UI update
          this.$forceUpdate();

          // Emit event that customizations were loaded
          this.$emit('customizations-loaded', customData);
        }

        return customData;
      } catch (error) {
        console.error('Error loading customization data:', error);
        return null;
      }
    },
    updateRelatedComponents() {
      // Emit event to notify parent that UI needs updating
      this.$emit('ui-update-needed');

      // Force update on this component
      this.$forceUpdate();
    },
    applyExistingCustomizations() {
      // Check if parent component passed any pre-loaded data via props
      if (this.speakers && this.speakers.length) {
        // Update internal speakers based on the props
        this.internalSpeakers = JSON.parse(JSON.stringify(this.speakers));

        // Log the changes
        console.log("Applied existing speaker customizations:", this.internalSpeakers);
      }
    },
    async saveSpeakerCustomizations() {
      try {
        // Format the speaker customizations data
        const speakerData = this.internalSpeakers.map((speaker, index) => ({
          original_name: this.speakers[index].name,
          custom_name: speaker.name,
          original_role: this.speakers[index].role,
          custom_role: speaker.role,
          voice_id: this.selectedVoices[index] || null,
          speaker_index: index
        }));

        // Create the customizations payload
        const customizations = {
          speakers: speakerData,
          background_settings: {
            use_custom_background: !!this.backgroundImage
          }
        };

        // Send to backend
        await API.post(
          `/user_customization/messages/${this.messageId}/speaker-customizations`,
          customizations
        );

      } catch (error) {
        console.error('Error saving speaker customizations:', error);
      }
    },
    // Toggle settings panel
    toggleSettings() {
      // Only available to pro users
      if (!this.isPro) {
        this.showTempMessageFn({
          message: 'Customization is only available for Pro users.',
          type: 'warning'
        });
        return;
      }
      this.showSettings = !this.showSettings;
    },

    // Get the active speaker object
    getActiveSpeakerObj() {
      if (!this.activeSpeaker) return null;
      return this.internalSpeakers.find(speaker => speaker.name === this.activeSpeaker) || {};
    },

    // Get the active speaker index
    getActiveSpeakerIndex() {
      if (!this.activeSpeaker) return -1;
      return this.internalSpeakers.findIndex(speaker => speaker.name === this.activeSpeaker);
    },

    // Get active speaker image
    getActiveSpeakerImage() {
      const index = this.getActiveSpeakerIndex();
      if (index === -1) return '';
      return this.getSpeakerImage(index);
    },

    // Get active speaker style
    getActiveSpeakerStyle() {
      const index = this.getActiveSpeakerIndex();
      if (index === -1) return {};
      const color = this.colors[index % this.colors.length];
      return {
        '--speaker-color': color.text,
        '--speaker-bg': color.bg
      };
    },

    // Handle background image upload
    async handleBackgroundUpload(event) {
      const file = event.target.files[0];
      if (!file) return;

      // Check if file is an image
      if (file.type.startsWith('image/') || file.name.endsWith('.webp')) {
        try {
          // Create a FormData object
          const formData = new FormData();
          formData.append('file', file);

          // Upload to backend
          const response = await API.post(`/user_customization/messages/${this.messageId}/background`, formData, {
            headers: {
              'Content-Type': 'multipart/form-data'
            }
          });

          // Update local state with the returned URL
          this.backgroundImage = response.data.url;

          // Emit event that background was uploaded
          this.$emit('background-uploaded', {
            imageUrl: response.data.url,
            id: response.data.id
          });
        } catch (error) {
          console.error('Error uploading background image:', error);
          // Add error handling UI if needed
        }
      }
    },

    // Get a readable name from voice ID
    getReadableVoiceName(voiceId) {
      if (!voiceId) return '';

      // Parse the voice ID into readable parts
      const parts = voiceId.split('_');
      if (parts.length < 2) return voiceId;

      let gender = '';
      let region = '';
      let name = parts[1];

      // Determine gender and region
      switch(parts[0]) {
        case 'af': gender = 'Female'; region = 'American'; break;
        case 'am': gender = 'Male'; region = 'American'; break;
        case 'bf': gender = 'Female'; region = 'British'; break;
        case 'bm': gender = 'Male'; region = 'British'; break;
        case 'ef': gender = 'Female'; region = 'Spanish'; break;
        case 'em': gender = 'Male'; region = 'Spanish'; break;
        case 'hf': gender = 'Female'; region = 'Hindi'; break;
        case 'hm': gender = 'Male'; region = 'Hindi'; break;
        case 'if': gender = 'Female'; region = 'Italian'; break;
        case 'im': gender = 'Male'; region = 'Italian'; break;
        case 'jf': gender = 'Female'; region = 'Japanese'; break;
        case 'jm': gender = 'Male'; region = 'Japanese'; break;
        case 'pf': gender = 'Female'; region = 'Portuguese'; break;
        case 'pm': gender = 'Male'; region = 'Portuguese'; break;
        case 'zf': gender = 'Female'; region = 'Chinese'; break;
        case 'zm': gender = 'Male'; region = 'Chinese'; break;
        default: return voiceId;
      }

      // Capitalize name
      name = name.charAt(0).toUpperCase() + name.slice(1);

      return `${name} (${region})`;
    },

    // Handle image upload
    async handleImageUpload(event, index) {
      const file = event.target.files[0];
      if (!file) return;

      // Check if file is an image
      if (file.type.startsWith('image/') || file.name.endsWith('.webp')) {
        try {
          // Create a FormData object
          const formData = new FormData();
          formData.append('file', file);
          formData.append('original_name', this.internalSpeakers[index].name);

          // Upload to backend
          const response = await API.post(
            `/user_customization/messages/${this.messageId}/speakers/${index}/image`,
            formData,
            {
              headers: {
                'Content-Type': 'multipart/form-data'
              }
            }
          );

          // Create a unique key using messageId and index
          const uniqueKey = `${this.messageId}_${index}`;

          // Update the image in the component state
          this.$set(this.uploadedImages, uniqueKey, response.data.url);

          // Emit event with all necessary info
          this.$emit('image-uploaded', {
            messageId: this.messageId,
            index,
            speakerName: this.internalSpeakers[index].name,
            imageUrl: response.data.url,
            id: response.data.id
          });
        } catch (error) {
          console.error('Error uploading speaker image:', error);
          // Add error handling UI if needed
        }
      }
    },

    // Get the image for a speaker (uploaded or fallback)
    getSpeakerImage(index) {
      // Create a unique key using messageId and index
      const uniqueKey = `${this.messageId}_${index}`;

      // Return the uploaded image for this specific combination if it exists
      return this.uploadedImages[uniqueKey] || this.allImages[index % this.allImages.length];
    },

    // Handle name change
    async handleNameChange(index) {
      // Get the original name before the change
      const oldName = this.speakers[index].name;
      const newName = this.internalSpeakers[index].name;

      // Only proceed if there's actually a change
      if (oldName === newName) return;

      // Emit event with updated speaker data
      this.$emit('name-changed', {
        index,
        newName,
        oldName
      });

      // Save the customizations
      await this.saveSpeakerCustomizations();

      // Add a small delay to ensure the parent component has time to process the change
      setTimeout(() => {
        // If the active speaker was renamed, update the reference
        if (this.activeSpeaker === oldName) {
          this.$emit('speaker-selected', newName);
        }
      }, 100);
    },

    // Handle role change
    async handleRoleChange(index) {
      // Emit event with updated role data
      this.$emit('role-changed', {
        index,
        speakerName: this.internalSpeakers[index].name,
        newRole: this.internalSpeakers[index].role,
        oldRole: this.speakers[index].role
      });

      // Save the customizations
      await this.saveSpeakerCustomizations();
    },

    // Handle voice change
    async handleVoiceChange(index) {
      // Emit event with voice selection data
      this.$emit('voice-changed', {
        index,
        speakerName: this.internalSpeakers[index].name,
        voiceId: this.selectedVoices[index] || ''
      });

      // Save the customizations
      await this.saveSpeakerCustomizations();
    },

    getTextColor(index) {
      if (index < 0) return '#1E88E5'; // Default color
      const color = this.colors[index % this.colors.length];
      return color.text;
    },
  }
}
</script>

<style scoped>
/* Main container - simplified for natural look */
.podcast-container {
  position: relative;
  width: 100%;
  height: auto;
  display: flex;
  flex-direction: column;
  background-image: linear-gradient(rgba(0, 0, 0, 0.5), rgba(0, 0, 0, 0.5)), url('/static/Gemini_Generated_Image_kn0ojzkn0ojzkn0o.jpeg');
  background-size: cover;
  background-position: center;
  overflow: hidden;
  border-radius: 12px;
  transition: background 0.5s ease;
  background-color: #f8fafc;
  max-height: 100%;
}

.theme--dark .podcast-container {
  background-color: #0f172a;
}

/* Main stage area - more spacious and natural */
.main-stage {
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 1.5rem;
  overflow: hidden;
  width: 100%;
  max-height: 40vh; /* Prevent excessive height */
}

.active-speaker-container {
  width: 100%;
  max-width: 800px; /* Wider to use more horizontal space */
  margin: 0 auto;
  transition: all 0.3s ease;
}

/* Removed box styling for a more natural look */
.active-speaker-wrapper {
  position: relative;
  display: flex;
  align-items: center;
  padding: 1rem 1.5rem;
  transition: all 0.3s ease;
  gap: 1.5rem;
}

.active-speaker-image {
  width: 220px;
  height: 220px;
  border-radius: 50%;
  object-fit: cover;
  transition: all 0.3s ease;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

/* Make image glow subtly when speaking */
.is-playing .active-speaker-image {
  box-shadow: 0 0 20px 5px rgba(255, 255, 255, 0.3);
}

.active-speaker-info {
  text-align: left;
  flex: 1;
}

.active-speaker-name {
  display: block;
  font-size: 1.75rem;
  font-weight: 600;
  color: white;
  margin-bottom: 0.25rem;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);
}

.active-speaker-role {
  display: block;
  font-size: 1.125rem;
  color: rgba(255, 255, 255, 0.85);
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.3);
}

/* Audio visualizer - more subtle and integrated */
.audio-visualizer {
  display: flex;
  align-items: flex-end;
  height: 50px;
  gap: 3px;
}

.visualizer-bar {
  width: 4px;
  height: 5px;
  background-color: white;
  border-radius: 2px;
  animation: equalizer 1.2s ease-in-out infinite;
}

.visualizer-bar:nth-child(1) { animation-duration: 0.9s; }
.visualizer-bar:nth-child(2) { animation-duration: 1.3s; }
.visualizer-bar:nth-child(3) { animation-duration: 1.0s; }
.visualizer-bar:nth-child(4) { animation-duration: 1.5s; }
.visualizer-bar:nth-child(5) { animation-duration: 1.2s; }

/* Speaker strip - redesigned for better flow */
.speaker-strip {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 1rem;
  padding: 1rem;
  background: rgba(0, 0, 0, 0.7);
  z-index: 10;
  overflow-x: auto;
  width: 100%;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
}

.theme--dark .speaker-strip {
  background: rgba(15, 23, 42, 0.7);
}

/* Speaker thumbnails - more natural design */
.speaker-thumbnail {
  display: flex;
  align-items: center;
  cursor: pointer;
  transition: all 0.3s ease;
  padding: 0.5rem 0.75rem;
  border-radius: 24px;
  background: transparent;
}

.speaker-thumbnail.active {
  background: rgba(255, 255, 255, 0.15);
}

.speaker-thumbnail:hover {
  background: rgba(255, 255, 255, 0.1);
}

.thumbnail-wrapper {
  position: relative;
  width: 40px;
  height: 40px;
}

.thumbnail-image {
  width: 100%;
  height: 100%;
  border-radius: 50%;
  object-fit: cover;
  transition: all 0.3s ease;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.2);
}

.speaker-thumbnail.active .thumbnail-image {
  box-shadow: 0 0 0 2px var(--speaker-color, white);
}

/* Wave indicator when speaking - simplified */
.thumbnail-wave-indicator {
  position: absolute;
  bottom: -4px;
  right: -4px;
  background: rgba(0, 0, 0, 0.6);
  border-radius: 8px;
  padding: 2px;
  display: flex;
  gap: 2px;
}

.thumbnail-wave-bar {
  width: 3px;
  height: 3px;
  border-radius: 1px;
  background: white;
  animation: thumbnailWave 1s ease-in-out infinite;
}

.thumbnail-wave-bar:nth-child(1) { animation-delay: -0.3s; }
.thumbnail-wave-bar:nth-child(2) { animation-delay: -0.15s; }
.thumbnail-wave-bar:nth-child(3) { animation-delay: 0s; }

@keyframes thumbnailWave {
  0%, 100% { height: 3px; }
  50% { height: 7px; }
}

.thumbnail-name {
  margin-left: 0.75rem;
  font-size: 0.875rem;
  color: rgba(255, 255, 255, 0.9);
}

.speaker-thumbnail.active .thumbnail-name {
  color: white;
  font-weight: 500;
}

/* Settings panel - keeping the existing style */
.settings-panel {
  position: fixed;
  top: 0.3rem;
  right: 4rem;
  z-index: 100;
}

@media screen and (max-width: 768px) {
  .settings-panel {
    top: 1rem;
  }
}

.settings-toggle {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  background-color: #f1f5f9;
  border: none;
  border-radius: 8px;
  padding: 0.5rem 1rem;
  font-size: 0.875rem;
  font-weight: 500;
  color: #e53e3e;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.05);
}

.theme--dark .settings-toggle {
  background-color: #1e293b;
  color: #e53e3e;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.2);
}

.settings-toggle:hover {
  background-color: #e2e8f0;
}

.theme--dark .settings-toggle:hover {
  background-color: #334155;
}

.settings-content {
  position: absolute;
  top: calc(100% + 0.5rem);
  right: 0;
  width: 400px;
  max-height: 80vh;
  overflow-y: auto;
  background-color: white;
  border-radius: 12px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
  padding: 1.5rem;
  animation: slideIn 0.3s ease;
  z-index: 101;
}

.theme--dark .settings-content {
  background-color: #1e293b;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3);
}

.settings-section {
  margin-bottom: 2rem;
}

.settings-section:last-child {
  margin-bottom: 0;
}

.settings-title {
  font-size: 1.125rem;
  font-weight: 600;
  margin-bottom: 1rem;
  color: #334155;
}

.theme--dark .settings-title {
  color: #e2e8f0;
}

/* Background upload - keep existing styles */
.background-upload {
  width: 100%;
}

.upload-bg-label {
  display: block;
  cursor: pointer;
  width: 100%;
}

.bg-preview-container {
  width: 100%;
  height: 140px;
  border-radius: 8px;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 2px dashed #cbd5e0;
  background-color: #f8fafc;
  transition: all 0.3s ease;
}

.theme--dark .bg-preview-container {
  border-color: #475569;
  background-color: #1e293b;
}

.bg-preview-container:hover {
  border-color: #3b82f6;
  background-color: #f1f5f9;
}

.theme--dark .bg-preview-container:hover {
  border-color: #60a5fa;
  background-color: #334155;
}

.bg-preview {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.bg-placeholder {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0.75rem;
  color: #64748b;
}

.theme--dark .bg-placeholder {
  color: #94a3b8;
}

.bg-placeholder svg {
  opacity: 0.7;
}

/* Speaker customization grid - keep existing styles */
.speakers-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(180px, 1fr));
  gap: 1.5rem;
  margin-top: 1rem;
}

.speaker-customize-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.75rem;
}

.speaker-image-upload {
  width: 100%;
}

.preview-container {
  width: 80px;
  height: 80px;
  border-radius: 50%;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 2px dashed #cbd5e0;
  background-color: #f8fafc;
  margin: 0 auto;
  transition: all 0.3s ease;
}

.theme--dark .preview-container {
  border-color: #475569;
  background-color: #1e293b;
}

.preview-container:hover {
  border-color: #3b82f6;
  transform: scale(1.05);
}

.theme--dark .preview-container:hover {
  border-color: #60a5fa;
}

.image-preview {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.upload-placeholder {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  font-size: 0.75rem;
  color: #64748b;
  text-align: center;
  padding: 0.5rem;
}

.theme--dark .upload-placeholder {
  color: #94a3b8;
}

.file-input {
  position: absolute;
  width: 0;
  height: 0;
  opacity: 0;
  overflow: hidden;
}

.speaker-fields {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  width: 100%;
}

.name-input, .role-input, .voice-select {
  width: 100%;
  padding: 0.5rem;
  font-size: 0.875rem;
  border: 1px solid #cbd5e0;
  border-radius: 6px;
  transition: all 0.3s;
  background-color: #ffffff;
  color: #1a202c;
}

.theme--dark .name-input,
.theme--dark .role-input,
.theme--dark .voice-select {
  border-color: #475569;
  background-color: #1e293b;
  color: #e2e8f0;
}

.name-input:focus,
.role-input:focus {
  border-color: #3b82f6;
  outline: none;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.15);
}

.theme--dark .name-input:focus,
.theme--dark .role-input:focus {
  border-color: #60a5fa;
  box-shadow: 0 0 0 3px rgba(96, 165, 250, 0.2);
}

.voice-select {
  cursor: pointer;
  appearance: none;
  background-image: url("data:image/svg+xml;charset=utf-8,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3E%3Cpath stroke='%236B7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3E%3C/svg%3E");
  background-position: right 0.5rem center;
  background-repeat: no-repeat;
  background-size: 1.25em 1.25em;
  padding-right: 2rem;
}

.theme--dark .voice-select {
  background-image: url("data:image/svg+xml;charset=utf-8,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3E%3Cpath stroke='%239CA3AF' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3E%3C/svg%3E");
}

/* Animations */
@keyframes fadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}

@keyframes slideIn {
  from {
    opacity: 0;
    transform: translateY(-20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes equalizer {
  0%, 100% { height: 5px; }
  50% { height: 30px; }
}

/* Responsive styles */
@media (max-width: 768px) {
  .main-stage {
    padding: 1rem;
  }

  .active-speaker-wrapper {
    padding: 0.75rem;
    flex-direction: column;
    text-align: center;
  }

  .active-speaker-image {
    width: 220px;
    height: 220px;
    margin-bottom: 0.75rem;
  }

  .active-speaker-info {
    text-align: center;
  }

  .active-speaker-name {
    font-size: 1.25rem;
  }

  .active-speaker-role {
    font-size: 0.875rem;
  }

  .audio-visualizer {
    margin-top: 0.75rem;
    height: 30px;
  }

  .settings-content {
    width: 300px;
    max-height: 70vh;
  }

  .speakers-grid {
    grid-template-columns: repeat(auto-fill, minmax(140px, 1fr));
    gap: 1rem;
  }

  .speaker-strip {
    padding: 0.75rem 0.5rem;
  }

  .speaker-thumbnail {
    padding: 0.35rem 0.5rem;
  }

  .thumbnail-wrapper {
    width: 32px;
    height: 32px;
  }

  .thumbnail-name {
    font-size: 0.75rem;
    margin-left: 0.5rem;
  }
}

@media (max-width: 480px) {
  .active-speaker-image {
    width: 150px;
    height: 150px;
  }

  .settings-content {
    width: calc(100vw - 2rem);
    right: -1rem;
    max-height: 60vh;
  }

  .speakers-grid {
    grid-template-columns: 1fr 1fr;
  }

  .speaker-strip {
    justify-content: space-around;
  }
}
.active-speaker-image-container {
  position: relative;
  width: 220px;
  height: 220px;
  border-radius: 50%;
  overflow: visible; /* Allow the wave indicator to be visible outside the container */
}

.active-speaker-image {
  width: 100%;
  height: 100%;
  border-radius: 50%;
  object-fit: cover;
  transition: all 0.3s ease;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

/* Wave indicator for the main speaker image */
.active-image-wave-indicator {
  position: absolute;
  bottom: 10px;
  right: 10px;
  background: rgba(0, 0, 0, 0.6);
  border-radius: 12px;
  padding: 4px;
  display: flex;
  gap: 3px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.3);
  z-index: 5;
}

.main-wave-bar {
  width: 4px;
  height: 4px;
  border-radius: 2px;
  background: white;
  animation: mainWaveAnimation 1.2s ease-in-out infinite;
}

.main-wave-bar:nth-child(1) { animation-delay: -0.4s; }
.main-wave-bar:nth-child(2) { animation-delay: -0.3s; }
.main-wave-bar:nth-child(3) { animation-delay: -0.2s; }
.main-wave-bar:nth-child(4) { animation-delay: -0.1s; }
.main-wave-bar:nth-child(5) { animation-delay: 0s; }

@keyframes mainWaveAnimation {
  0%, 100% { height: 4px; }
  50% { height: 16px; }
}

/* Update responsive styles for main speaker image */
@media (max-width: 768px) {
  .active-speaker-image-container {
    width: 160px;
    height: 160px;
    margin-bottom: 0.75rem;
  }

  .active-image-wave-indicator {
    bottom: 5px;
    right: 5px;
    padding: 3px;
    gap: 2px;
  }

  .main-wave-bar {
    width: 3px;
  }
}

@media (max-width: 480px) {
  .active-speaker-image-container {
    width: 120px;
    height: 120px;
  }

  .active-image-wave-indicator {
    padding: 2px;
    gap: 2px;
  }

  .main-wave-bar {
    width: 2px;
  }
}
</style>
